import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live-syncs the browser tab title + favicon with the operator's
 * store-settings branding.
 *
 * Reads from ``/api/branding`` (public, no-auth) once on mount and
 * again whenever ``version`` changes — so the Settings page can force a
 * re-fetch after the manager saves a new logo or website title. Falls
 * back to the shipped favicon and "Crest POS" title if the API is
 * unreachable or fields are blank.
 *
 * Mounts once from ``App``, so every route benefits — including the
 * login page (which runs pre-auth) and printer preview windows.
 */
const DEFAULT_TITLE = "Crest POS";

const _setFavicon = (url) => {
  if (!url) return;
  // Replace every existing favicon link tag with one pointing at the
  // uploaded logo (data URL or external). If none exist, create one.
  const heads = document.querySelectorAll(
    "link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
  );
  const rels = ["icon", "shortcut icon", "apple-touch-icon"];
  heads.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  rels.forEach((rel) => {
    const link = document.createElement("link");
    link.rel = rel;
    link.href = url;
    // Data URLs need an explicit mime; otherwise let the browser sniff.
    if (url.startsWith("data:image/png")) link.type = "image/png";
    else if (url.startsWith("data:image/webp")) link.type = "image/webp";
    else if (url.startsWith("data:image/svg")) link.type = "image/svg+xml";
    document.head.appendChild(link);
  });
};

export default function useSiteBranding(version = 0) {
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/branding`)
      .then(({ data }) => {
        if (cancelled) return;
        const title =
          (data?.website_title && data.website_title.trim()) ||
          (data?.company_name && data.company_name.trim()) ||
          DEFAULT_TITLE;
        document.title = title;
        if (data?.logo_url) _setFavicon(data.logo_url);
      })
      .catch(() => {
        // Silent — keep whatever's already in the HTML.
      });
    return () => {
      cancelled = true;
    };
  }, [version]);
}
